import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import PracticeUserAccountStatusSection from "components/features/practiceManagement/PracticeUserAccountStatusSection";
import PracticeUserBranchesSection from "components/features/practiceManagement/PracticeUserBranchesSection";
import PracticeUserInformationSection from "components/features/practiceManagement/PracticeUserInformationSection";
import UnsavedChangesModal from "components/features/profile/UnsavedChangesModal";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { practiceUsersApi } from "apis";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { PracticeProfileDetailDto } from "libs/cellera-sdk";
import { FirebaseStorageService } from "helpers/firebaseStorage";
import { useToast } from "contexts/ToastContext";
import {
  userProfileSchema,
  UserProfileFormData,
  providerSpecialtyOptions,
  ProviderSpecialtyType,
} from "validations/userSchemas";

const PracticeUserDetailPage: React.FC = () => {
  const { practiceUserId } = useParams<{ practiceUserId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const initialFormDataRef = useRef<UserProfileFormData | null>(null);

  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState<
    string | null
  >(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );

  const { data, isLoading, error, refetch } = useQuery<
    PracticeProfileDetailDto,
    Error
  >({
    queryKey: ["practice-user-detail", practiceUserId],
    queryFn: async () => {
      const res = await practiceUsersApi.getPracticeProfileByUserId({
        userId: practiceUserId || "",
      });
      return res;
    },
    enabled: Boolean(practiceUserId),
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: UserProfileFormData) => {
      return await practiceUsersApi.updatePracticeUser({
        userId: practiceUserId || "",
        practiceProfileUpdateDto: {
          firstName: formData.firstName,
          middleName: formData.middleName,
          lastName: formData.lastName,
          npi: formData.npi,
          phoneNumber: formData.phoneNumber ?? "",
          profileImage: formData.profileImage as string,
          specialty: formData.specialties as string[],
          ...(formData.isActive !== undefined && {
            isActive: formData.isActive,
          }),
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["practice-user-detail", practiceUserId],
      });
      queryClient.invalidateQueries({
        queryKey: ["practiceUsers"],
      });
      showSuccess(
        "Profile Updated",
        "Practice user profile has been updated successfully.",
      );
    },
    onError: (error) => {
      console.error("API Error updating practice user:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update profile. Please try again.";
      showError("Update Failed", errorMessage);
    },
  });

  const userProfileForm = useForm({
    resolver: yupResolver(userProfileSchema),
    defaultValues: {
      id: "",
      email: "",
      profileImage: null,
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      npi: "",
      specialties: [],
      isActive: undefined,
    },
    mode: "onChange",
  });

  // Function to compare form data and detect changes
  const checkForChanges = useCallback((currentData: UserProfileFormData) => {
    if (!initialFormDataRef.current) return false;

    const initial = initialFormDataRef.current;

    return (
      initial.firstName !== currentData.firstName ||
      initial.middleName !== currentData.middleName ||
      initial.lastName !== currentData.lastName ||
      initial.phoneNumber !== currentData.phoneNumber ||
      initial.npi !== currentData.npi ||
      JSON.stringify(initial.specialties) !==
        JSON.stringify(currentData.specialties) ||
      initial.profileImage !== currentData.profileImage ||
      (initial.isActive !== undefined &&
        initial.isActive !== currentData.isActive)
    );
  }, []);

  // Watch for form changes
  const watchedValues = userProfileForm.watch();

  useEffect(() => {
    if (initialFormDataRef.current) {
      const hasChanges = checkForChanges(watchedValues);
      setHasUnsavedChanges(hasChanges);
    }
  }, [watchedValues, checkForChanges]);

  useEffect(() => {
    if (data) {
      const validSpecialties = (data.specialty || []).filter(
        (spec: string): spec is ProviderSpecialtyType =>
          providerSpecialtyOptions.includes(spec as ProviderSpecialtyType),
      );

      const formData: UserProfileFormData = {
        id: data.id || "",
        email: data.email || "",
        profileImage: data.profilePicture || null,
        firstName: data.firstName || "",
        middleName: data.middleName || "",
        lastName: data.lastName || "",
        phoneNumber: data.phoneNumber || "",
        npi: data.npi || "",
        specialties: validSpecialties,
        isActive: data.isActive,
      };

      userProfileForm.reset(formData);
      initialFormDataRef.current = formData;
      setCurrentProfileImageUrl(data.profilePicture ?? null);
      setHasUnsavedChanges(false);
    } else {
      userProfileForm.reset({});
      initialFormDataRef.current = null;
      setCurrentProfileImageUrl(null);
      setHasUnsavedChanges(false);
    }
  }, [data, userProfileForm]);

  // Navigation blocking
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // returnValue is deprecated but still needed for browser compatibility
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleNavigation = useCallback(
    (path: string) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(path);
        setShowUnsavedModal(true);
      } else {
        navigate(path);
      }
    },
    [hasUnsavedChanges, navigate],
  );

  const handleSaveAndGo = useCallback(async () => {
    const currentData = userProfileForm.getValues();
    await updateMutation.mutateAsync(currentData);

    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);

    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate, updateMutation, userProfileForm]);

  const handleCancelNavigation = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  }, []);

  const onSubmit = useCallback(
    async (data: UserProfileFormData) => {
      let profileImageUrl = currentProfileImageUrl ?? "";
      // Handle profile image upload if it's a File object
      if (data.profileImage instanceof File) {
        try {
          profileImageUrl = await FirebaseStorageService.uploadProfileImage(
            data.profileImage,
          );
        } catch {
          throw new Error("Failed to upload profile image. Please try again.");
        }
      }
      await updateMutation.mutateAsync({
        ...data,
        profileImage: profileImageUrl,
      });

      // Update initial form data and reset unsaved changes
      initialFormDataRef.current = data;
      setHasUnsavedChanges(false);
    },
    [currentProfileImageUrl, updateMutation],
  );

  const handleBack = useCallback(() => {
    handleNavigation(EPath.PRACTICE_MANAGEMENT);
  }, [handleNavigation]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const renderContent = () => {
    if (error) {
      return (
        <Alert
          severity="error"
          action={
            <Box
              component="button"
              onClick={() => refetch()}
              style={{
                background: "none",
                border: "none",
                color: colors.primary.main,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Retry
            </Box>
          }
        >
          {error.message || "Failed to load practice user information"}
        </Alert>
      );
    }

    if (!data) {
      return <Alert severity="info">Practice user not found.</Alert>;
    }

    return (
      <FormProvider {...userProfileForm}>
        <form
          onSubmit={userProfileForm.handleSubmit(onSubmit)}
          style={{ position: "relative" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              p: 3,
              position: "relative",
            }}
          >
            <PracticeUserInformationSection
              profileImage={data.profilePicture ?? ""}
            />

            <Box sx={{ position: "relative", zIndex: 0 }}>
              <PracticeUserBranchesSection branches={data.branches || []} />
            </Box>

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <PracticeUserAccountStatusSection />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 4,
                position: "relative",
                zIndex: 10,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                sx={{ px: 6, py: 2, borderRadius: "9999px", minWidth: 120 }}
                disabled={updateMutation.isPending || !hasUnsavedChanges}
                startIcon={
                  updateMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {updateMutation.isPending ? "Saving..." : "Save"}
              </Button>
              <Button
                type="button"
                variant="outlined"
                sx={{ px: 6, py: 2, borderRadius: "9999px", minWidth: 120 }}
                onClick={() => {
                  userProfileForm.reset();
                }}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Practice User Details">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Practice User Details"
      onNavigate={handleNavigation}
      breadcrumbs={[
        {
          label: "Practice Management",
          href: EPath.PRACTICE_MANAGEMENT,
          order: 0,
        },
        {
          label: "Practice Management",
          href: EPath.PRACTICE_MANAGEMENT,
          order: 1,
        },
      ]}
      lastBreadcrumb="Practice User Details"
      onBack={handleBack}
      onRefresh={handleRefresh}
    >
      <Box sx={{ p: 2 }}>{renderContent()}</Box>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onCancel={handleCancelNavigation}
        onSaveAndGo={handleSaveAndGo}
        isSubmitting={updateMutation.isPending}
      />
    </DashboardLayout>
  );
};

export default PracticeUserDetailPage;
